import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStats } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Eye, X, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { toast } from 'sonner';
import RiskSection from '../components/RiskSection';
import BacktestSection from '../components/BacktestSection';
import StatsSection from '../components/StatsSection';
import ExigenciaSection from '../components/ExigenciaSection';
import { LSTMSection } from '../components/LSTMSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [roiModal, setRoiModal] = useState(false);
  const [pendingModal, setPendingModal] = useState(false);
  const [leagueModal, setLeagueModal] = useState(null); // { key, name, flag }
  const [roiData, setRoiData] = useState(null);
  const [pendingPicks, setPendingPicks] = useState([]);
  const [leagueStats, setLeagueStats] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [clvMetrics, setClvMetrics] = useState(null);
  const [apiMonitor, setApiMonitor] = useState(null);

  const leagues = [
    { key: 'la_liga', name: 'La Liga', flag: '🇪🇸' },
    { key: 'premier_league', name: 'Premier League', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
    { key: 'bundesliga', name: 'Bundesliga', flag: '🇩🇪' },
    { key: 'serie_a', name: 'Serie A', flag: '🇮🇹' },
    { key: 'ligue_1', name: 'Ligue 1', flag: '🇫🇷' },
    { key: 'champions_league', name: 'Champions League', flag: '🏆' },
    { key: 'mls', name: 'MLS', flag: '🇺🇸' },
    { key: 'liga_mx', name: 'Liga MX', flag: '🇲🇽' },
  ];

  // Function to fetch all data
  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const timestamp = Date.now(); // Cache buster
      const noCacheHeaders = {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      };
      
      const [statsRes, historyRes, clvRes, apiMonitorRes] = await Promise.all([
        getStats(),
        fetch(`${API_URL}/api/picks/history/results?days=14&_t=${timestamp}`, {
          headers: noCacheHeaders
        }),
        fetch(`${API_URL}/api/clv/metrics?days=30&_t=${timestamp}`, {
          headers: noCacheHeaders
        }),
        fetch(`${API_URL}/api/system/api-monitor?_t=${timestamp}`, {
          headers: noCacheHeaders
        })
      ]);
      
      setStats(statsRes.data);
      setLastUpdate(new Date());
      
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setHistory(historyData);
      }
      
      if (clvRes.ok) {
        const clvData = await clvRes.json();
        setClvMetrics(clvData);
      }
      
      if (apiMonitorRes.ok) {
        const apiData = await apiMonitorRes.json();
        setApiMonitor(apiData);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load + auto-refresh every 60 seconds
  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    
    // Refresh when tab becomes visible again (mobile background/foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData]);

  const fetchRoiDetails = async () => {
    setLoadingModal(true);
    setRoiModal(true);
    try {
      const token = localStorage.getItem('token');
      const [evolutionRes, monthlyRes] = await Promise.all([
        fetch(`${API_URL}/api/stats/bankroll-evolution?days=90`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/stats/monthly-summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const evolution = await evolutionRes.json();
      const monthly = await monthlyRes.json();
      
      setRoiData({ evolution, monthly });
    } catch (error) {
      console.error('Error fetching ROI:', error);
    } finally {
      setLoadingModal(false);
    }
  };

  const fetchLeagueStats = async (league) => {
    setLoadingModal(true);
    setLeagueModal(league);
    setLeagueStats(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/stats/by-league/${league.key}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeagueStats(data);
      }
    } catch (error) {
      console.error('Error fetching league stats:', error);
    } finally {
      setLoadingModal(false);
    }
  };

  const fetchPendingPicks = async () => {
    setLoadingModal(true);
    setPendingModal(true);
    try {
      const token = localStorage.getItem('token');
      const timestamp = Date.now();
      // Fetch picks with status=pending (result is null, not yet verified)
      const response = await fetch(`${API_URL}/api/picks?days=30&status=pending&_t=${timestamp}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const data = await response.json();
      // data should now only contain pending picks (result === null)
      setPendingPicks(data);
    } catch (error) {
      console.error('Error fetching pending:', error);
    } finally {
      setLoadingModal(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const toggleDay = (date) => {
    setExpandedDay(expandedDay === date ? null : date);
  };

  const fixIncorrectResults = async () => {
    setFixing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/picks/fix-incorrect-results`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.fixed_picks?.length > 0) {
        toast.success(`¡Corregidos ${data.fixed_picks.length} picks!`);
        window.location.reload();
      } else {
        toast.info('No hay picks para corregir');
      }
    } catch (error) {
      toast.error('Error al corregir');
    } finally {
      setFixing(false);
    }
  };

  const updateRealOdds = async () => {
    setFixing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/picks/update-real-odds?force_all=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.updated_count > 0) {
        toast.success(`¡${data.updated_count} cuotas actualizadas!`);
        window.location.reload();
      } else {
        toast.info('Todas las cuotas ya están actualizadas');
      }
    } catch (error) {
      toast.error('Error al actualizar cuotas');
    } finally {
      setFixing(false);
    }
  };

  const leagueLabels = {
    la_liga: 'La Liga',
    premier_league: 'Premier',
    bundesliga: 'Bundesliga',
    serie_a: 'Serie A',
    ligue_1: 'Ligue 1',
    champions_league: 'UCL',
    mls: 'MLS',
    liga_mx: 'Liga MX',
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      <main className="pt-16 pb-20 px-3 sm:px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pt-2">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">
                Panel de <span className="text-[#00E599]">Gestión</span>
              </h1>
              <p className="text-sm text-zinc-400 flex items-center gap-2 flex-wrap">
                Pronósticos deportivos
                {stats?.stats_baseline_since && (
                  <Link
                    to="/admin/markets-insights"
                    className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25"
                    data-testid="dashboard-baseline-badge"
                    title={`Todos los KPIs cuentan desde ${stats.stats_baseline_since}.\nClic para ver comparativa pre/post.`}
                  >
                    KPIs desde {stats.stats_baseline_since}
                  </Link>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={fixIncorrectResults}
                disabled={fixing}
                variant="outline"
                size="sm"
                className="border-[#F43F5E] text-[#F43F5E] hover:bg-[#F43F5E]/10 h-9 text-xs"
              >
                {fixing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                L→W
              </Button>
              <Button 
                onClick={updateRealOdds}
                disabled={fixing}
                variant="outline"
                size="sm"
                className="border-[#EAB308] text-[#EAB308] hover:bg-[#EAB308]/10 h-9 text-xs"
              >
                {fixing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Cuotas
              </Button>
              <Button 
                onClick={() => fetchData(true)}
                disabled={refreshing}
                variant="outline"
                size="sm"
                className="border-zinc-600 text-zinc-400 hover:bg-zinc-800 h-9 text-xs"
                data-testid="refresh-stats-btn"
              >
                {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              </Button>
              <Link to="/picks">
                <Button className="btn-primary h-9 text-xs" size="sm">
                  Ver Picks
                </Button>
              </Link>
            </div>
            {lastUpdate && (
              <p className="text-[10px] text-zinc-500 mt-1 text-right">
                Actualizado: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>

          {/* Tabs Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1 h-11">
              <TabsTrigger 
                value="dashboard" 
                className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black px-4"
                data-testid="tab-dashboard"
              >
                Dashboard
              </TabsTrigger>
              <TabsTrigger 
                value="risk" 
                className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black px-4"
                data-testid="tab-risk"
              >
                Riesgo
              </TabsTrigger>
              <TabsTrigger 
                value="backtest" 
                className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black px-4"
                data-testid="tab-backtest"
              >
                Backtest
              </TabsTrigger>
              <TabsTrigger 
                value="exigencia" 
                className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black px-4"
                data-testid="tab-exigencia"
              >
                Exigencia
              </TabsTrigger>
              <TabsTrigger 
                value="lstm" 
                className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black px-4"
                data-testid="tab-lstm"
              >
                LSTM
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab Content */}
            <TabsContent value="dashboard" className="mt-6">
              {/* Main Stats */}
              <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Win Rate Total</span>
              <p className="text-2xl sm:text-3xl font-heading font-bold text-[#00E599] mt-1">
                {stats ? `${stats.simple.win_rate}%` : '--'}
              </p>
              <span className="text-xs text-zinc-400">
                {stats?.simple.wins || 0}W - {stats?.simple.losses || 0}L
              </span>
            </div>
            
            {/* ROI Card with Button */}
            <button 
              onClick={fetchRoiDetails}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-left hover:border-[#6366F1]/50 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">ROI Total</span>
                <Eye className="w-4 h-4 text-zinc-600 group-hover:text-[#6366F1] transition-colors" />
              </div>
              <p className={`text-2xl sm:text-3xl font-heading font-bold mt-1 ${stats?.simple.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                {stats ? `${stats.simple.roi}%` : '--'}
              </p>
              <span className="text-xs text-zinc-400">Toca para ver detalles</span>
            </button>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Profit Total</span>
              <p className="text-2xl sm:text-3xl font-heading font-bold text-[#EAB308] mt-1">
                {stats ? `${stats.simple.profit_units > 0 ? '+' : ''}${stats.simple.profit_units}u` : '--'}
              </p>
              <span className="text-xs text-zinc-400">Unidades netas</span>
            </div>
            
            {/* Pendientes Card with Button */}
            <button 
              onClick={fetchPendingPicks}
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-left hover:border-[#6366F1]/50 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Pendientes</span>
                <Eye className="w-4 h-4 text-zinc-600 group-hover:text-[#6366F1] transition-colors" />
              </div>
              <p className="text-2xl sm:text-3xl font-heading font-bold text-[#6366F1] mt-1">
                {stats?.pending_to_send || '--'}
              </p>
              <span className="text-xs text-zinc-400">Toca para ver picks</span>
            </button>
          </div>

          {/* ROI by Sport - NEW SECTION */}
          {stats?.by_sport && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              {/* Football ROI */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Fútbol</span>
                </div>
                <p className={`text-xl font-heading font-bold ${stats.by_sport.football.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                  {stats.by_sport.football.roi >= 0 ? '+' : ''}{stats.by_sport.football.roi}%
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-zinc-500">{stats.by_sport.football.wins}W-{stats.by_sport.football.losses}L</span>
                  <span className={`text-[10px] ${stats.by_sport.football.profit_units >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {stats.by_sport.football.profit_units >= 0 ? '+' : ''}{stats.by_sport.football.profit_units}u
                  </span>
                </div>
              </div>
              
              {/* Combined ROI */}
              <div className="bg-gradient-to-br from-[#6366F1]/20 to-zinc-900/50 border border-[#6366F1]/30 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Total</span>
                </div>
                <p className={`text-xl font-heading font-bold ${stats.by_sport.combined.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                  {stats.by_sport.combined.roi >= 0 ? '+' : ''}{stats.by_sport.combined.roi}%
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-zinc-500">{stats.by_sport.combined.wins}W-{stats.by_sport.combined.losses}L</span>
                  <span className={`text-[10px] ${stats.by_sport.combined.profit_units >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {stats.by_sport.combined.profit_units >= 0 ? '+' : ''}{stats.by_sport.combined.profit_units}u
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* CLV Metrics Card - Enhanced */}
          {clvMetrics && clvMetrics.picks_with_clv > 0 && (
            <div className="bg-gradient-to-r from-[#EAB308]/20 to-[#EAB308]/5 border border-[#EAB308]/30 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-heading text-base font-bold text-white">CLV (Closing Line Value)</h3>
                  <p className="text-[10px] text-zinc-500">¿Obtienes mejores cuotas que el mercado?</p>
                </div>
                <Badge className={`${clvMetrics.avg_clv >= 0 ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'} border-0 text-xs`}>
                  {clvMetrics.avg_clv >= 0 ? '+' : ''}{clvMetrics.avg_clv}% avg
                </Badge>
              </div>
              
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-zinc-500 uppercase">Picks</p>
                  <p className="text-lg font-bold text-white">{clvMetrics.picks_with_clv}</p>
                </div>
                <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-zinc-500 uppercase">CLV+</p>
                  <p className="text-lg font-bold text-[#00E599]">{clvMetrics.positive_clv_pct}%</p>
                </div>
                <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-zinc-500 uppercase">Ganadores</p>
                  <p className={`text-lg font-bold ${clvMetrics.won_avg_clv >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {clvMetrics.won_avg_clv >= 0 ? '+' : ''}{clvMetrics.won_avg_clv}%
                  </p>
                </div>
                <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-zinc-500 uppercase">Perdedores</p>
                  <p className={`text-lg font-bold ${clvMetrics.lost_avg_clv >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {clvMetrics.lost_avg_clv >= 0 ? '+' : ''}{clvMetrics.lost_avg_clv}%
                  </p>
                </div>
              </div>

              {/* Best/Worst Markets */}
              {clvMetrics.by_market && Object.keys(clvMetrics.by_market).length > 0 && (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {Object.entries(clvMetrics.by_market)
                    .sort((a, b) => b[1].avg_clv - a[1].avg_clv)
                    .slice(0, 2)
                    .map(([market, data]) => (
                      <div key={market} className={`rounded-lg p-2 text-xs ${data.avg_clv >= 0 ? 'bg-[#00E599]/10 border border-[#00E599]/20' : 'bg-[#F43F5E]/10 border border-[#F43F5E]/20'}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-300 truncate">{market}</span>
                          <span className={`font-bold ${data.avg_clv >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                            {data.avg_clv >= 0 ? '+' : ''}{data.avg_clv}%
                          </span>
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          {data.picks} picks • {data.positive_pct}% CLV+
                        </div>
                      </div>
                    ))}
                </div>
              )}
              
              <div className="flex items-center justify-between text-[10px] text-zinc-500 pt-2 border-t border-zinc-800">
                <span>Captura automática cada 30 min antes del partido</span>
                <span className={clvMetrics.clv_win_correlation > 0 ? 'text-[#00E599]' : 'text-zinc-400'}>
                  Correlación: {clvMetrics.clv_win_correlation > 0 ? '+' : ''}{(clvMetrics.clv_win_correlation * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          {/* Active Leagues - Clickeable */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
            <h3 className="font-heading text-sm font-bold text-white mb-3">Competiciones Activas</h3>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
              {leagues.map((league) => (
                <button
                  key={league.key}
                  onClick={() => league.navigateTo ? navigate(league.navigateTo) : fetchLeagueStats(league)}
                  className={`flex flex-col items-center p-2 rounded-lg hover:bg-zinc-700/50 active:scale-95 transition-all ${
                    league.isBasketball ? 'bg-[#F97316]/20 border border-[#F97316]/30' : 
                    league.key === 'mundial' ? 'bg-[#00E599]/20 border border-[#00E599]/30' : 'bg-zinc-800/50'
                  }`}
                >
                  <span className="text-lg">{league.flag}</span>
                  <span className="text-[10px] text-zinc-400 mt-1">{league.name.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ML Progress Card */}
          {stats?.ml_progress && (
            <div className="bg-gradient-to-r from-[#6366F1]/20 to-[#6366F1]/5 border border-[#6366F1]/30 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-base font-bold text-white">Progreso ML</h3>
                <Badge className={`${stats.ml_progress.ready_for_auto_adjust ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-[#6366F1]/20 text-[#6366F1]'} border-0 text-xs`}>
                  {stats.ml_progress.ready_for_auto_adjust ? 'Activo' : 'Acumulando'}
                </Badge>
              </div>
              
              <div className="flex items-center gap-3 mb-2">
                <Progress 
                  value={stats.ml_progress.percentage} 
                  className="flex-1 h-2 bg-zinc-800"
                />
                <span className="text-sm font-mono text-[#6366F1] font-bold min-w-[48px] text-right">
                  {stats.ml_progress.percentage}%
                </span>
              </div>
              
              <div className="flex items-center justify-between text-xs mb-3">
                <span className="text-zinc-400">
                  {stats.ml_progress.verified} / {stats.ml_progress.threshold} picks verificados
                </span>
                {!stats.ml_progress.ready_for_auto_adjust && (
                  <span className="text-[#6366F1]">
                    Faltan {stats.ml_progress.remaining} picks
                  </span>
                )}
              </div>
              
              {/* Desglose por tipo */}
              {stats.by_type && (
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-zinc-800">
                  <div className="text-center">
                    <span className="text-lg font-bold text-white">{stats.by_type.picks?.total || 0}</span>
                    <p className="text-[10px] text-zinc-500">Picks</p>
                  </div>
                  <div className="text-center">
                    <span className="text-lg font-bold text-white">{stats.by_type.combinadas?.total || 0}</span>
                    <p className="text-[10px] text-zinc-500">Combinadas</p>
                  </div>
                  <div className="text-center">
                    <span className="text-lg font-bold text-white">{stats.by_type.especiales?.total || 0}</span>
                    <p className="text-[10px] text-zinc-500">Especiales</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stats Section - Collapsible */}
          <div className="mb-6">
            <StatsSection embedded={true} />
          </div>

          {/* API Monitor Widget */}
          {apiMonitor && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6" data-testid="api-monitor-widget">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-bold text-white">Estado de APIs</h3>
                <button
                  onClick={async () => {
                    const token = localStorage.getItem('token');
                    try {
                      const res = await fetch(`${API_URL}/api/system/api-monitor/check`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (res.ok) {
                        const data = await res.json();
                        setApiMonitor({
                          the_odds_api: data.results?.the_odds_api,
                          api_sports: data.results?.api_sports
                        });
                        toast.success('Estado de APIs actualizado');
                      }
                    } catch (e) {
                      toast.error('Error actualizando estado');
                    }
                  }}
                  className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Actualizar
                </button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* api-sports.io (Primary) */}
                <div className={`rounded-lg p-3 ${
                  apiMonitor.api_sports?.status === 'active' 
                    ? 'bg-[#00E599]/10 border border-[#00E599]/30' 
                    : 'bg-zinc-950 border border-zinc-800'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-white">api-sports.io</span>
                    <Badge className={`text-[9px] ${
                      apiMonitor.api_sports?.status === 'active' 
                        ? 'bg-[#00E599]/20 text-[#00E599]' 
                        : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {apiMonitor.api_sports?.status === 'active' ? 'ACTIVA' : 'DESCONOCIDO'}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-zinc-500">Fuente primaria de cuotas</p>
                  {apiMonitor.api_sports?.requests_current && (
                    <p className="text-[10px] text-zinc-400 mt-1">
                      {apiMonitor.api_sports.requests_current}/{apiMonitor.api_sports.requests_limit} requests
                    </p>
                  )}
                </div>
                
                {/* The Odds API (Verification) */}
                <div className={`rounded-lg p-3 ${
                  apiMonitor.the_odds_api?.status === 'active' 
                    ? 'bg-[#00E599]/10 border border-[#00E599]/30' 
                    : apiMonitor.the_odds_api?.quota_status === 'exceeded'
                    ? 'bg-[#F43F5E]/10 border border-[#F43F5E]/30'
                    : 'bg-zinc-950 border border-zinc-800'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-white">The Odds API</span>
                    <Badge className={`text-[9px] ${
                      apiMonitor.the_odds_api?.status === 'active' 
                        ? 'bg-[#00E599]/20 text-[#00E599]' 
                        : apiMonitor.the_odds_api?.quota_status === 'exceeded'
                        ? 'bg-[#F43F5E]/20 text-[#F43F5E]'
                        : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {apiMonitor.the_odds_api?.status === 'active' ? 'ACTIVA' : 
                       apiMonitor.the_odds_api?.quota_status === 'exceeded' ? 'SIN CUOTA' : 'DESCONOCIDO'}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-zinc-500">Verificación cruzada</p>
                  {apiMonitor.the_odds_api?.last_successful_check && (
                    <p className="text-[10px] text-zinc-400 mt-1">
                      Última OK: {new Date(apiMonitor.the_odds_api.last_successful_check).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {apiMonitor.the_odds_api?.quota_status === 'exceeded' && (
                    <p className="text-[10px] text-[#F43F5E] mt-1">
                      Renueva tu plan en the-odds-api.com
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stats by Market - Mejor mercados */}
          {stats?.by_market && Object.keys(stats.by_market).length > 0 && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="font-heading text-base font-bold text-white">Rendimiento por Mercado</h3>
                {stats.by_market_baseline_since && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40"
                    data-testid="dashboard-market-baseline-badge"
                    title={`Solo se cuentan picks con match_date ≥ ${stats.by_market_baseline_since}.\nEl histórico anterior está archivado en /admin/markets-insights.`}
                  >
                    desde {stats.by_market_baseline_since}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {Object.entries(stats.by_market)
                  .sort((a, b) => b[1].total - a[1].total)
                  .slice(0, 6)
                  .map(([market, data]) => {
                    const marketLabels = {
                      'over_under_goles': 'Over/Under Goles',
                      'btts': 'BTTS',
                      '1x2': '1X2 Resultado',
                      'doble_oportunidad': 'Doble Oportunidad',
                      'tarjetas': 'Tarjetas',
                      'corners': 'Corners',
                      'over_under_otros': 'Over/Under Otros',
                      'unknown': 'Otros'
                    };
                    return (
                      <div key={market} className="flex items-center justify-between bg-zinc-950 rounded-lg p-2">
                        <span className="text-xs text-zinc-300">{marketLabels[market] || market}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-zinc-500">{data.wins}W-{data.losses}L</span>
                          <span className={`text-xs font-bold ${data.win_rate >= 55 ? 'text-[#00E599]' : data.win_rate >= 45 ? 'text-[#EAB308]' : 'text-[#F43F5E]'}`}>
                            {data.win_rate}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {stats.by_market_baseline_since && (
                <Link
                  to="/admin/markets-insights"
                  className="block text-[11px] text-amber-300 hover:text-amber-200 mt-3 text-center"
                  data-testid="dashboard-go-insights"
                >
                  Ver comparativa pre/post → /admin/markets-insights
                </Link>
              )}
            </div>
          )}

          {/* Channel Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <h3 className="font-heading text-sm font-bold text-white mb-0.5">Canal Gratuito</h3>
              <p className="text-[10px] text-zinc-500 mb-2">Para comunidad</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#00E599]">
                    {stats?.by_channel?.free?.wins || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Ganados</p>
                </div>
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#F43F5E]">
                    {stats?.by_channel?.free?.losses || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Perdidos</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900/50 border border-[#EAB308]/20 rounded-xl p-3">
              <h3 className="font-heading text-sm font-bold text-white mb-0.5">Canal VIP</h3>
              <p className="text-[10px] text-zinc-500 mb-2">Premium</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#00E599]">
                    {stats?.by_channel?.vip?.wins || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Ganados</p>
                </div>
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#F43F5E]">
                    {stats?.by_channel?.vip?.losses || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Perdidos</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900/50 border border-[#6366F1]/20 rounded-xl p-3">
              <h3 className="font-heading text-sm font-bold text-white mb-0.5">Combinadas</h3>
              <p className="text-[10px] text-zinc-500 mb-2">Múltiples</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#00E599]">
                    {stats?.by_type?.combinadas?.wins || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Ganadas</p>
                </div>
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#F43F5E]">
                    {stats?.by_type?.combinadas?.losses || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Perdidas</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900/50 border border-[#8B5CF6]/20 rounded-xl p-3">
              <h3 className="font-heading text-sm font-bold text-white mb-0.5">Especiales</h3>
              <p className="text-[10px] text-zinc-500 mb-2">Bet Builder</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#00E599]">
                    {stats?.by_type?.especiales?.wins || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Ganadas</p>
                </div>
                <div className="bg-zinc-950 rounded-lg p-2 text-center">
                  <span className="text-lg font-heading font-bold text-[#F43F5E]">
                    {stats?.by_type?.especiales?.losses || 0}
                  </span>
                  <p className="text-[9px] text-zinc-500 mt-0.5">Perdidas</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-3 mb-6">
            <Link to="/picks" className="block">
              <div className="bg-gradient-to-r from-[#00E599]/20 to-[#00E599]/5 border border-[#00E599]/30 rounded-xl p-4 active:scale-[0.98] transition-transform">
                <h3 className="font-heading text-base font-bold text-white">Pronósticos y Elección</h3>
                <p className="text-xs text-zinc-400">Gestiona picks y la elección del día</p>
              </div>
            </Link>
            <Link to="/admin/telegram" className="block">
              <div className="bg-zinc-900/50 border border-[#00E599]/30 rounded-xl p-4 active:scale-[0.98] transition-transform">
                <h3 className="font-heading text-base font-bold text-white">Canales Telegram</h3>
                <p className="text-xs text-zinc-400">FREE · VIP · Selección Ganadores</p>
              </div>
            </Link>
            <Link to="/admin/markets-insights" className="block">
              <div className="bg-zinc-900/50 border border-[#6366F1]/30 rounded-xl p-4 active:scale-[0.98] transition-transform">
                <h3 className="font-heading text-base font-bold text-white">Insights de Mercados</h3>
                <p className="text-xs text-zinc-400">ROI, CLV y edge por mercado</p>
              </div>
            </Link>
          </div>

          {/* History Section */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-white">Historial de Resultados</h2>
              {history?.accumulated && (
                <Badge className={`${history.accumulated.win_rate >= 55 ? 'bg-[#00E599]/20 text-[#00E599]' : history.accumulated.win_rate >= 45 ? 'bg-[#EAB308]/20 text-[#EAB308]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'} border-0 text-xs`}>
                  {history.accumulated.win_rate}%
                </Badge>
              )}
            </div>

            {history?.accumulated && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-zinc-950 rounded-lg p-2.5 text-center">
                  <span className="text-lg font-heading font-bold text-white">{history.accumulated.total}</span>
                  <p className="text-[10px] text-zinc-500">Total</p>
                </div>
                <div className="bg-zinc-950 rounded-lg p-2.5 text-center">
                  <span className="text-lg font-heading font-bold text-[#00E599]">{history.accumulated.won}</span>
                  <p className="text-[10px] text-zinc-500">Acertados</p>
                </div>
                <div className="bg-zinc-950 rounded-lg p-2.5 text-center">
                  <span className="text-lg font-heading font-bold text-[#F43F5E]">{history.accumulated.lost}</span>
                  <p className="text-[10px] text-zinc-500">Fallados</p>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#00E599] animate-spin" />
              </div>
            ) : history?.history?.length > 0 ? (
              <div className="space-y-2">
                {history.history.map((day) => (
                  <div key={day.date} className="border border-zinc-800 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleDay(day.date)}
                      className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-white">{formatDate(day.date)}</span>
                        <div className="flex items-center gap-1.5">
                          <Badge className="bg-[#00E599]/20 text-[#00E599] border-0 text-[10px] h-5">
                            {day.won}W
                          </Badge>
                          <Badge className="bg-[#F43F5E]/20 text-[#F43F5E] border-0 text-[10px] h-5">
                            {day.lost}L
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">{day.total} picks</span>
                        {expandedDay === day.date ? (
                          <ChevronUp className="w-4 h-4 text-zinc-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-zinc-500" />
                        )}
                      </div>
                    </button>

                    {expandedDay === day.date && (
                      <div className="p-3 space-y-2 bg-zinc-900/30">
                        {day.picks.map((pick, idx) => (
                          <div 
                            key={pick.id || idx}
                            className={`rounded-lg p-2.5 ${pick.result === 'won' ? 'bg-[#00E599]/10 border border-[#00E599]/20' : 'bg-[#F43F5E]/10 border border-[#F43F5E]/20'}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-white truncate">
                                  {pick.home_team} vs {pick.away_team}
                                </p>
                                <p className="text-[10px] text-zinc-400 mt-0.5">
                                  {pick.prediction}
                                </p>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] h-4">
                                  @{pick.odds?.toFixed(2)}
                                </Badge>
                                <Badge className={`${pick.result === 'won' ? 'bg-[#00E599] text-black' : 'bg-[#F43F5E] text-white'} border-0 text-[10px] h-4`}>
                                  {pick.result === 'won' ? 'WIN' : 'LOSS'}
                                </Badge>
                              </div>
                            </div>
                            
                            {pick.actual_result && (
                              <p className="text-[10px] text-zinc-400 mt-1">
                                Resultado: {pick.actual_result}
                              </p>
                            )}
                            
                            {pick.result === 'lost' && pick.failure_explanation && (
                              <div className="mt-2 pt-2 border-t border-[#F43F5E]/20">
                                <p className="text-[10px] text-[#F43F5E]/80 font-medium mb-0.5">¿Por qué falló?</p>
                                <p className="text-[10px] text-zinc-400">{pick.failure_explanation}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-zinc-500 text-sm">Sin resultados verificados aún</p>
              </div>
            )}
          </div>
            </TabsContent>

            {/* Risk Tab Content */}
            <TabsContent value="risk" className="mt-6">
              <RiskSection embedded={true} />
            </TabsContent>

            {/* Backtest Tab Content */}
            <TabsContent value="backtest" className="mt-6">
              <BacktestSection embedded={true} />
            </TabsContent>
            <TabsContent value="exigencia" className="mt-6">
              <ExigenciaSection />
            </TabsContent>
            <TabsContent value="lstm" className="mt-6">
              <LSTMSection />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* ROI Detail Modal */}
      <Dialog open={roiModal} onOpenChange={setRoiModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg mx-3 max-h-[85vh] overflow-hidden p-0">
          <div className="p-4 border-b border-zinc-800">
            <DialogTitle className="text-white font-heading text-lg">Detalles de ROI</DialogTitle>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            {loadingModal ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
              </div>
            ) : roiData ? (
              <div className="space-y-6">
                {/* Main ROI Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-zinc-950 rounded-lg p-4 text-center">
                    <p className={`text-3xl font-heading font-bold ${stats?.simple.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                      {stats?.simple.roi >= 0 ? '+' : ''}{stats?.simple.roi}%
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">ROI Total</p>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-4 text-center">
                    <p className={`text-3xl font-heading font-bold ${stats?.simple.profit_units >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                      {stats?.simple.profit_units >= 0 ? '+' : ''}{stats?.simple.profit_units}u
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">Profit Total</p>
                  </div>
                </div>

                {/* Evolution Chart */}
                {roiData.evolution?.evolution?.length > 0 && (
                  <div className="bg-zinc-950 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-4">Evolución del Bankroll</h4>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={roiData.evolution.evolution}>
                          <defs>
                            <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#00E599" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#00E599" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: '#71717a', fontSize: 10 }}
                            tickFormatter={(v) => v.substring(5)}
                          />
                          <YAxis 
                            tick={{ fill: '#71717a', fontSize: 10 }}
                            tickFormatter={(v) => `${v}u`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#18181b', 
                              border: '1px solid #27272a',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value) => [`${value}u`, 'Profit']}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="cumulative_profit" 
                            stroke="#00E599" 
                            fillOpacity={1}
                            fill="url(#colorProfit)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Summary Stats */}
                {roiData.evolution?.summary && (
                  <div className="bg-zinc-950 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Resumen de Rachas</h4>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#00E599]">{roiData.evolution.summary.best_winning_streak}</p>
                        <p className="text-[10px] text-zinc-500">Mejor racha W</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#F43F5E]">{roiData.evolution.summary.worst_losing_streak}</p>
                        <p className="text-[10px] text-zinc-500">Peor racha L</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-lg font-bold ${roiData.evolution.summary.current_streak >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                          {roiData.evolution.summary.current_streak >= 0 ? '+' : ''}{roiData.evolution.summary.current_streak}
                        </p>
                        <p className="text-[10px] text-zinc-500">Racha actual</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Monthly Breakdown */}
                {roiData.monthly?.monthly?.length > 0 && (
                  <div className="bg-zinc-950 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Por Mes</h4>
                    <div className="space-y-2">
                      {roiData.monthly.monthly.slice(0, 6).map((m) => (
                        <div key={m.month} className="flex items-center justify-between text-xs bg-zinc-900 rounded p-2">
                          <span className="text-zinc-400">{m.month}</span>
                          <span className="text-white">{m.wins}W-{m.losses}L</span>
                          <span className={`font-bold ${m.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                            {m.roi >= 0 ? '+' : ''}{m.roi}%
                          </span>
                          <span className={`${m.profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                            {m.profit >= 0 ? '+' : ''}{m.profit}u
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-zinc-500 py-8">No hay datos disponibles</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Picks Modal */}
      <Dialog open={pendingModal} onOpenChange={setPendingModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg mx-3 max-h-[85vh] overflow-hidden p-0">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <DialogTitle className="text-white font-heading text-lg">
              Picks Pendientes ({pendingPicks.length})
            </DialogTitle>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            {loadingModal ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
              </div>
            ) : pendingPicks.length > 0 ? (
              <div className="space-y-2">
                {pendingPicks.map((pick) => (
                  <div key={pick.id} className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {pick.home_team} vs {pick.away_team}
                        </p>
                        <p className="text-xs text-zinc-400 mt-0.5">{pick.prediction}</p>
                      </div>
                      <Badge className={`text-[10px] ${
                        pick.channel === 'vip' ? 'bg-[#EAB308]/20 text-[#EAB308]' : 'bg-zinc-700 text-zinc-300'
                      }`}>
                        {pick.channel?.toUpperCase() || 'FREE'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-500">
                      <span className="text-[#00E599] font-bold">@{pick.odds?.toFixed(2)}</span>
                      <span>{leagueLabels[pick.league] || pick.league}</span>
                      <span>
                        {new Date(pick.match_date).toLocaleDateString('es-ES', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-zinc-500">No hay picks pendientes</p>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t border-zinc-800">
            <Link to="/picks">
              <Button className="w-full btn-primary h-11">
                Ir a Pronósticos
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* League Stats Modal */}
      <Dialog open={!!leagueModal} onOpenChange={() => setLeagueModal(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg mx-3 max-h-[85vh] overflow-hidden p-0">
          <div className="p-4 border-b border-zinc-800">
            <DialogTitle className="text-white font-heading text-lg flex items-center gap-2">
              <span className="text-2xl">{leagueModal?.flag}</span>
              {leagueModal?.name}
            </DialogTitle>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            {loadingModal ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
              </div>
            ) : leagueStats ? (
              <div className="space-y-4">
                {/* Main Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-zinc-950 rounded-lg p-4 text-center">
                    <p className="text-3xl font-heading font-bold text-[#00E599]">{leagueStats.wins}</p>
                    <p className="text-xs text-zinc-500 mt-1">Aciertos</p>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-4 text-center">
                    <p className="text-3xl font-heading font-bold text-[#F43F5E]">{leagueStats.losses}</p>
                    <p className="text-xs text-zinc-500 mt-1">Fallos</p>
                  </div>
                </div>

                {/* Win Rate & ROI */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-zinc-950 rounded-lg p-4 text-center">
                    <p className={`text-2xl font-heading font-bold ${leagueStats.win_rate >= 55 ? 'text-[#00E599]' : leagueStats.win_rate >= 45 ? 'text-[#EAB308]' : 'text-[#F43F5E]'}`}>
                      {leagueStats.win_rate}%
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">Win Rate</p>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-4 text-center">
                    <p className={`text-2xl font-heading font-bold ${leagueStats.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                      {leagueStats.roi >= 0 ? '+' : ''}{leagueStats.roi}%
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">ROI</p>
                  </div>
                </div>

                {/* Profit */}
                <div className="bg-zinc-950 rounded-lg p-4 text-center">
                  <p className={`text-2xl font-heading font-bold ${leagueStats.profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {leagueStats.profit >= 0 ? '+' : ''}{leagueStats.profit}u
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">Profit (unidades)</p>
                </div>

                {/* Best Markets in League */}
                {leagueStats.markets && Object.keys(leagueStats.markets).length > 0 && (
                  <div className="bg-zinc-950 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Mercados en esta liga</h4>
                    <div className="space-y-2">
                      {Object.entries(leagueStats.markets)
                        .sort((a, b) => b[1].total - a[1].total)
                        .map(([market, data]) => {
                          const marketLabels = {
                            'over_under_goles': 'Over/Under',
                            'btts': 'BTTS',
                            '1x2': '1X2',
                            'doble_oportunidad': 'Doble Op.',
                            'tarjetas': 'Tarjetas',
                            'corners': 'Corners'
                          };
                          return (
                            <div key={market} className="flex items-center justify-between bg-zinc-900 rounded p-2">
                              <span className="text-xs text-zinc-300">{marketLabels[market] || market}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-zinc-500">{data.wins}W-{data.losses}L</span>
                                <span className={`text-xs font-bold ${data.win_rate >= 55 ? 'text-[#00E599]' : data.win_rate >= 45 ? 'text-[#EAB308]' : 'text-[#F43F5E]'}`}>
                                  {data.win_rate}%
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Recent Picks */}
                {leagueStats.recent_picks && leagueStats.recent_picks.length > 0 && (
                  <div className="bg-zinc-950 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Últimos picks</h4>
                    <div className="space-y-2">
                      {leagueStats.recent_picks.slice(0, 5).map((pick) => (
                        <div key={pick.id || `${pick.home_team}-${pick.away_team}-${pick.match_date}`} className={`flex items-center justify-between p-2 rounded ${pick.result === 'won' ? 'bg-[#00E599]/10' : 'bg-[#F43F5E]/10'}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white truncate">{pick.home_team} vs {pick.away_team}</p>
                            <p className="text-[10px] text-zinc-500">{pick.prediction}</p>
                          </div>
                          <Badge className={`text-[10px] ${pick.result === 'won' ? 'bg-[#00E599] text-black' : 'bg-[#F43F5E] text-white'}`}>
                            {pick.result === 'won' ? 'W' : 'L'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-zinc-500">No hay datos para esta liga</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
